import {observable, computed, action, autorun} from 'mobx';
import storage from 'lib/localStorage';
import RendererStore from './Pixi/RendererStore';
import WhiteSpaceStore from './Components/WhiteSpaceStore';

export const sheetTypeEnum = {
  SHEETWISE: "P/S",
  WORK_AND_TURN: "Ü/V",
  WORK_AND_TUMBLE: "Ü/P"
};

export default class SheetCalculatorStore {
  rendererStore = null;
  store = {
    @observable sheet: {
      width: 640,
      height: 460,
      bottom: 15,
      top: 10,
      left: 3,
      right: 3
    },
    @observable page: {
      width: 148,
      height: 210,
      amount: 2
    },
    @observable bleed: {
      horizontal: '3',
      vertical: '3'
    },
    @observable options: {
      type: sheetTypeEnum.SHEETWISE
    },
    @observable settings: {
      brochure: {
        top: 'exactly|0',
        bottom: 'exactly|0',
        right: 'exactly|0',
        left: 'exactly|0'
      }
    },
    @observable canvasWidth: 0,
  };
  brochureWhiteSpaces = {};

  constructor() {
    this.load();
    this.persist();
    this.rendererStore = new RendererStore(this);
    Object.keys(this.store.settings.brochure).forEach(key => {
      this.brochureWhiteSpaces[key] = new WhiteSpaceStore(this.store.settings.brochure, key);
    });
  }

  @computed get options() {
    return {
      type: this.store.options.type
    }
  }

  @computed get canvas() {
    const canvasWidth = Math.min(this.store.canvasWidth, 800);
    return {
      width: canvasWidth,
      height: (canvasWidth * this.sheet.height) / this.sheet.width,
      scale: canvasWidth / this.store.sheet.width
    }
  }

  @computed get sheet() {
    return {
      width: this.store.sheet.width,
      height: this.store.sheet.height,
      bottom: this.store.sheet.bottom,
      top: this.store.sheet.top,
      left: this.store.sheet.left,
      right: this.store.sheet.right,
      area: this.store.sheet.width * this.store.sheet.height,
      printable: {
        width: this.store.sheet.width - this.store.sheet.left - this.store.sheet.right,
        height: this.store.sheet.height - this.store.sheet.bottom - this.store.sheet.top
      }
    }
  }

  @computed get page() {
    return {
      width: this.store.page.width,
      height: this.store.page.height,
      amount: this.store.page.amount,
      area: this.store.page.width * this.store.page.height
    }
  }

  @computed get bleed() {
    return {
      horizontal: this.store.bleed.horizontal,
      vertical: this.store.bleed.vertical,
      asArray: {
        horizontal: this.store.bleed.horizontal.split(',').map(val => parseInt(val) || 0),
        vertical: this.store.bleed.vertical.split(',').map(val => parseInt(val) || 0)
      }
    }
  }

  @computed get signature() {
    const start = performance.now();

    const fits = this.fits;
    const page = this.page;
    const sheet = this.sheet;
    const bleeds = this.bleed.asArray;
    const options = this.store.options;
    const rotation = fits.type === 'upright' ? 0 : 90;
    const pages = [];

    let pageWidth = page.width;
    let pageHeight = page.height;
    if (rotation % 180 !== 0) {
      pageWidth = page.height;
      pageHeight = page.width;
    }

    let y = pageHeight / 2;
    let pageRotation;
    let pageNr;
    for (let i = 0; i < fits.vertical; i++) {
      const verticalBleedIndex = i % bleeds.vertical.length;
      let x = pageWidth / 2;
      for (let j = 0; j < fits.horizontal; j++) {
        pageRotation = rotation;
        pageNr = 1;
        if (options.type === sheetTypeEnum.WORK_AND_TURN) {
          if (j >= (fits.horizontal / 2)) {
            if (rotation % 180 !== 0) pageRotation = rotation + 180;
            if (page.amount >= 2) pageNr = 2;
          }
        } else if (options.type === sheetTypeEnum.WORK_AND_TUMBLE) {
          if (i < (fits.vertical / 2)) {
            if (rotation % 180 === 0) pageRotation = rotation + 180;
            if (page.amount >= 2) pageNr = 2;
          }
        }

        const horizontalBleedIndex = j % bleeds.horizontal.length;
        pages.push({
          x,
          y,
          rotation: pageRotation,
          pageNr: pageNr,
          width: page.width,
          height: page.height
        });
        x += pageWidth + bleeds.horizontal[horizontalBleedIndex];
      }
      y += pageHeight + bleeds.vertical[verticalBleedIndex];
    }

    const signatureWidth = pages[pages.length - 1].x + (pageWidth / 2);
    const signatureHeight = pages[pages.length - 1].y + (pageHeight / 2);

    const timeTaken = performance.now() - start;
    console.log('@computed signature took', timeTaken);

    return {
      pages,
      width: signatureWidth,
      height: signatureHeight,
      x: (sheet.printable.width - signatureWidth) / 2 + sheet.left,
      y: (sheet.printable.height - signatureHeight) / 2 + sheet.top,
    };
  }

  @computed get fits() {
    let workAndTurn = this.store.options.type === sheetTypeEnum.WORK_AND_TURN;
    let workAndTumble = this.store.options.type === sheetTypeEnum.WORK_AND_TUMBLE;
    const upright = {
      horizontal: this.fitsAxis(this.sheet.printable.width, this.page.width, this.bleed.asArray.horizontal, workAndTurn),
      vertical: this.fitsAxis(this.sheet.printable.height, this.page.height, this.bleed.asArray.vertical, workAndTumble)
    };
    const rotated = {
      horizontal: this.fitsAxis(this.sheet.printable.width, this.page.height, this.bleed.asArray.horizontal, workAndTurn),
      vertical: this.fitsAxis(this.sheet.printable.height, this.page.width, this.bleed.asArray.vertical, workAndTumble)
    };
    if (upright.horizontal * upright.vertical >= rotated.horizontal * rotated.vertical) {
      return {
        type: 'upright',
        horizontal: upright.horizontal,
        vertical: upright.vertical,
        total: upright.horizontal * upright.vertical
      };
    } else {
      return {
        type: 'rotated',
        horizontal: rotated.horizontal,
        vertical: rotated.vertical,
        total: rotated.horizontal * rotated.vertical
      };
    }
  }

  fitsAxis = (sheetWidth, pageWidth, bleeds, type) => {
    if (pageWidth > sheetWidth) return 0;
    let currentWidth = pageWidth;
    let i = 1;
    while (currentWidth <= sheetWidth) {
      const bleedIndex = (i - 1) % bleeds.length;
      currentWidth += pageWidth + bleeds[bleedIndex];
      if (currentWidth <= sheetWidth) i++;
    }
    if (type && (i % 2 !== 0)) return i - 1;
    return i;
  };

  @action update = (observable, property, value) => {
    // console.log(`updating ${observable}.${property} to ${parseInt(value)}`);
    this.store[observable][property] = parseInt(value);
  };

  @action updateBleed = (property, value) => {
    this.store.bleed[property] = value;
  };

  @action updateCanvasWidth = width => {
    this.store.canvasWidth = width;
  };

  @action updateType = type => {
    this.store.options.type = type;
  };

  @action load() {
    const stored = storage.get('SheetCalculatorStore') || {};
    console.log('got stored data', stored);
    Object.keys(stored).forEach(key => this.store[key] = stored[key]);
  }

  @computed get toJson() {
    return this.store;
  }

  persist() {
    autorun(() => {
      storage.set('SheetCalculatorStore', this.toJson)
    })
  }
}



// WEBPACK FOOTER //
// ./src/views/SheetCalculator/SheetCalculatorStore.js